import { useEffect, useState } from "react";
import { Save, RotateCcw, Plus, Trash2, Globe, Undo2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getCostLibrary, updateCostLibrary } from "@/lib/api";
import { EXTERIOR_CATEGORIES } from "@/lib/exteriorTemplates";
import { SYSTEMS_CATEGORIES } from "@/lib/systemsTemplates";
import { currencyMultiplier, kindMultiplier, REGION_KIND_MULT } from "@/lib/regionalPresets";
import { constructionSummary } from "@/lib/constructionTypes";
import { toast } from "sonner";

// Single source of truth — the regional presets pull their per-kind
// multipliers from REGION_KIND_MULT. Keeping the metadata here (label,
// currency code) so the preset cards can render nicely.
const REGIONAL_PRESETS = [
  { code: "USA", label: "United States",   currency: "USD" },
  { code: "CAN", label: "Canada",          currency: "CAD" },
  { code: "UK",  label: "United Kingdom",  currency: "GBP" },
  { code: "EU",  label: "Eurozone",        currency: "EUR" },
  { code: "AUS", label: "Australia",       currency: "AUD" },
  { code: "NZ",  label: "New Zealand",     currency: "NZD" },
  { code: "ZA",  label: "South Africa",    currency: "ZAR" },
];

// Mirrors frontend/src/lib/roomTemplates.js base rates, now with units AND kind
// so per-currency / per-kind multipliers can be applied at preset time.
const ROOM_DEFAULTS = {
  bathroom: [
    { name: "Demolition",    kind: "labour",   unit: "room", rate: 800 },
    { name: "Waterproofing", kind: "material", unit: "sqm",  rate: 120 },
    { name: "Tiling",        kind: "mixed",    unit: "sqm",  rate: 180 },
    { name: "Sanitaryware",  kind: "material", unit: "set",  rate: 2500 },
    { name: "Vanity",        kind: "material", unit: "item", rate: 1800 },
    { name: "Paint",         kind: "mixed",    unit: "sqm",  rate: 35 },
    { name: "Lighting",      kind: "material", unit: "item", rate: 400 },
  ],
  kitchen: [
    { name: "Demolition",    kind: "labour",   unit: "room", rate: 1200 },
    { name: "Cabinetry",     kind: "material", unit: "lm",   rate: 1200 },
    { name: "Benchtop",      kind: "material", unit: "lm",   rate: 650 },
    { name: "Splashback",    kind: "material", unit: "sqm",  rate: 220 },
    { name: "Appliances",    kind: "material", unit: "set",  rate: 4500 },
    { name: "Plumbing",      kind: "labour",   unit: "item", rate: 900 },
    { name: "Electrical",    kind: "labour",   unit: "item", rate: 850 },
    { name: "Paint",         kind: "mixed",    unit: "sqm",  rate: 35 },
    { name: "Flooring",      kind: "mixed",    unit: "sqm",  rate: 150 },
  ],
  bedroom: [
    { name: "Flooring",      kind: "mixed",    unit: "sqm",  rate: 130 },
    { name: "Paint",         kind: "mixed",    unit: "sqm",  rate: 30 },
    { name: "Doors",         kind: "material", unit: "item", rate: 480 },
    { name: "Wardrobe",      kind: "material", unit: "lm",   rate: 600 },
    { name: "Lighting",      kind: "material", unit: "item", rate: 220 },
  ],
  lounge: [
    { name: "Flooring",      kind: "mixed",    unit: "sqm",  rate: 135 },
    { name: "Paint",         kind: "mixed",    unit: "sqm",  rate: 30 },
    { name: "Ceiling",       kind: "mixed",    unit: "sqm",  rate: 55 },
    { name: "Lighting",      kind: "material", unit: "item", rate: 280 },
    { name: "Skirting/Trim", kind: "mixed",    unit: "lm",   rate: 38 },
  ],
  laundry: [
    { name: "Cabinetry",     kind: "material", unit: "lm",   rate: 900 },
    { name: "Benchtop",      kind: "material", unit: "lm",   rate: 520 },
    { name: "Tiling",        kind: "mixed",    unit: "sqm",  rate: 150 },
    { name: "Plumbing",      kind: "labour",   unit: "item", rate: 650 },
    { name: "Paint",         kind: "mixed",    unit: "sqm",  rate: 32 },
  ],
  hallway: [
    { name: "Flooring",      kind: "mixed",    unit: "sqm",  rate: 130 },
    { name: "Paint",         kind: "mixed",    unit: "sqm",  rate: 30 },
    { name: "Lighting",      kind: "material", unit: "item", rate: 200 },
  ],
  dining: [
    { name: "Flooring",      kind: "mixed",    unit: "sqm",  rate: 135 },
    { name: "Paint",         kind: "mixed",    unit: "sqm",  rate: 30 },
    { name: "Lighting",      kind: "material", unit: "item", rate: 320 },
  ],
  office: [
    { name: "Flooring",      kind: "mixed",    unit: "sqm",  rate: 130 },
    { name: "Paint",         kind: "mixed",    unit: "sqm",  rate: 30 },
    { name: "Built-in Desk", kind: "material", unit: "lm",   rate: 650 },
    { name: "Lighting",      kind: "material", unit: "item", rate: 220 },
  ],
};

const UNIT_OPTIONS = ["sqm", "lm", "item", "set", "room", "days", "hours"];

function cryptoRandomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "ci-" + Math.random().toString(36).slice(2);
}

// Scale a built-in (USD) base rate by the currency hint so the *displayed*
// default rate matches what the rest of the app generates. If no override is
// set, this is the value users see (and would re-save if they hit Save).
// `kind` is optional — when provided (labour/material/mixed) we use the
// per-kind multiplier so the Library row default matches what
// `effectiveRate()` produces everywhere else in the app. Rows without a kind
// fall back to the mixed multiplier.
function scaledDefault(baseRate, currency, kind) {
  if (!currency) return Math.round(Number(baseRate) || 0);
  return Math.round((Number(baseRate) || 0) * (kind ? kindMultiplier(currency, kind) : currencyMultiplier(currency)));
}

// Project-scoped Cost Library editor. Mounts inside the Project dashboard as a
// tab. When `project` is provided the component derives its currency hint and
// construction summary from that project; on save it calls `onSaved(lib)` so
// the dashboard can refresh its cached costLibrary state.
export default function CostLibrary({ projectId, project, onSaved }) {
  const [lib, setLib] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const currencyHint = project?.currency || null;
  const constructionHint = project ? constructionSummary(project) : null;

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        setLoading(true);
        const data = await getCostLibrary(projectId);
        setLib(data);
      } catch {
        toast.error("Could not load library");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const save = async () => {
    try {
      setSaving(true);
      const saved = await updateCostLibrary(projectId, {
        room_rates: lib.room_rates,
        custom_room_items: lib.custom_room_items,
        exterior_rates: lib.exterior_rates,
        systems_rates: lib.systems_rates,
        extension_rates: lib.extension_rates,
      });
      setLib(saved);
      onSaved?.(saved);
      toast.success("Cost library saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = () => {
    if (!window.confirm("Reset all overrides and custom items to built-in defaults?")) return;
    setLib({ ...lib, room_rates: {}, custom_room_items: {}, exterior_rates: {}, systems_rates: {} });
    toast.success("Reset — click Save to persist");
  };

  const setExteriorRate = (key, val) => setLib({ ...lib, exterior_rates: { ...lib.exterior_rates, [key]: val } });
  const setSystemsRate = (key, val) => setLib({ ...lib, systems_rates: { ...lib.systems_rates, [key]: val } });
  const resetExteriorRate = (key) => {
    const next = { ...(lib.exterior_rates || {}) };
    delete next[key];
    setLib({ ...lib, exterior_rates: next });
  };
  const resetSystemsRate = (key) => {
    const next = { ...(lib.systems_rates || {}) };
    delete next[key];
    setLib({ ...lib, systems_rates: next });
  };
  const setRoomItemRate = (roomType, itemName, val) => {
    const next = { ...(lib.room_rates[roomType] || {}), [itemName]: val };
    setLib({ ...lib, room_rates: { ...lib.room_rates, [roomType]: next } });
  };
  const resetRoomItemRate = (roomType, itemName) => {
    const next = { ...(lib.room_rates?.[roomType] || {}) };
    delete next[itemName];
    setLib({ ...lib, room_rates: { ...lib.room_rates, [roomType]: next } });
  };
  const addCustomItem = (roomType) => {
    const cur = lib.custom_room_items?.[roomType] || [];
    const next = [...cur, { id: cryptoRandomId(), name: "New item", unit: "item", rate: 0 }];
    setLib({ ...lib, custom_room_items: { ...(lib.custom_room_items || {}), [roomType]: next } });
  };
  const updateCustomItem = (roomType, id, patch) => {
    const cur = lib.custom_room_items?.[roomType] || [];
    const next = cur.map((i) => (i.id === id ? { ...i, ...patch } : i));
    setLib({ ...lib, custom_room_items: { ...(lib.custom_room_items || {}), [roomType]: next } });
  };
  const deleteCustomItem = (roomType, id) => {
    const cur = lib.custom_room_items?.[roomType] || [];
    const next = cur.filter((i) => i.id !== id);
    setLib({ ...lib, custom_room_items: { ...(lib.custom_room_items || {}), [roomType]: next } });
  };

  const applyRegionalPreset = (preset) => {
    const matchesProjectCurrency = preset.currency === currencyHint;
    // When the preset's currency matches the project currency, the per-kind
    // multipliers already produce the correct rates automatically — writing
    // them again as explicit overrides would just light up every Reset button
    // for rates the user never changed. Instead, clear any existing overrides
    // so rows fall back to the live per-kind defaults.
    if (matchesProjectCurrency) {
      if (!window.confirm(
        `Reset this project to the ${preset.label} regional defaults? Any custom rate overrides you've made will be cleared so every row shows the region's derived rate again. Custom items are preserved.`
      )) return;
      setLib({
        ...lib,
        room_rates: {},
        exterior_rates: {},
        systems_rates: {},
      });
      toast.success(`${preset.label} defaults restored — rates now follow the ${preset.currency} per-kind multipliers. Click Save to persist.`);
      return;
    }

    // Cross-currency case: user is explicitly overriding with another
    // region's rates (rare). Write explicit overrides for every row so the
    // rates are pinned regardless of this project's currency.
    const km = REGION_KIND_MULT[preset.currency] || REGION_KIND_MULT.USD;
    if (!window.confirm(
      `Apply ${preset.label} preset? Rates will be pinned in ${preset.currency} using labour ${km.labour}× / material ${km.material}× / mixed ${km.mixed}× multipliers. Existing rate overrides will be replaced; custom items are preserved.\n\n(Your project currency is ${currencyHint || "USD"} — typically you'd just switch project currency on the dashboard instead.)`
    )) return;
    const newRoom = {};
    Object.entries(ROOM_DEFAULTS).forEach(([roomType, items]) => {
      newRoom[roomType] = {};
      items.forEach((it) => {
        const m = km[it.kind] ?? km.mixed;
        newRoom[roomType][it.name] = Math.round(it.rate * m);
      });
    });
    // Exterior + Systems categories don't carry a kind today — apply the
    // 'mixed' multiplier as the safe default. Users can fine-tune per row.
    const newExt = {};
    EXTERIOR_CATEGORIES.forEach((c) => { newExt[c.key] = Math.round(c.baseRate * km.mixed); });
    const newSys = {};
    SYSTEMS_CATEGORIES.forEach((c) => { newSys[c.key] = Math.round(c.baseRate * km.mixed); });
    setLib({
      ...lib,
      room_rates: newRoom,
      exterior_rates: newExt,
      systems_rates: newSys,
    });
    toast.success(`${preset.label} applied — rates pinned in ${preset.currency}. Click Save to persist.`);
  };

  if (loading || !lib) {
    return <div className="grid place-items-center text-[#A8A29E] text-sm py-16">Loading library…</div>;
  }

  return (
    <div className="space-y-6 fc-fade-in" data-testid="cost-library-page">
      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="library-tab-header">
        <div>
          <h2 className="fc-heading text-lg text-[#1C1C1C]">Cost Library</h2>
          <p className="text-xs text-[#A8A29E] mt-0.5">
            Per-project rate overrides. Edits here apply only to <strong className="text-[#1C1C1C]">{project?.name || "this project"}</strong>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="library-reset-btn"
            onClick={resetAll}
            className="text-xs text-[#78716C] hover:text-[#1C1C1C] border border-black/[0.08] hover:border-black/[0.2] rounded-md px-2.5 py-1.5 transition-colors flex items-center gap-1"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
          <Button
            data-testid="library-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] h-9 px-3"
          >
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
        {currencyHint && (
          <div
            className="rounded-lg bg-sage-50 border border-sage-200 px-4 py-3 text-sm text-sage-700 flex items-start gap-3"
            data-testid="library-currency-banner"
          >
            <Globe className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <strong>Rates below are entered in {currencyHint}</strong> per the unit shown next to each field
              (e.g. <span className="fc-mono">{currencyHint} / sqm</span>). Enter the cost of <em>one unit</em>,
              not the total budget for that line — totals are calculated for you on each tab.
              {constructionHint && (
                <div className="mt-1.5 text-[11px] text-sage-700/85" data-testid="library-construction-hint">
                  Your latest project is <strong>{constructionHint}</strong> — Extension shell &amp; Exterior items
                  are auto-multiplied to reflect that build style. Library rates here are the un-multiplied baseline.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Regional presets */}
        <div className="fc-card p-5" data-testid="regional-presets">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-black/[0.04] border border-black/[0.06] grid place-items-center text-[#27272A]">
                <Globe className="w-4 h-4" />
              </div>
              <div>
                <div className="fc-heading text-[#1C1C1C] text-[15px]">Regional Presets</div>
                <div className="text-xs text-[#A8A29E] mt-0.5 max-w-lg">
                  One-click starting points for major property markets. Rates are denominated in the region's local currency. Numbers are approximations — verify for your market, then fine-tune per room.
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="regional-preset-buttons">
            {REGIONAL_PRESETS.map((p) => {
              const active = p.currency === currencyHint;
              return (
                <button
                  key={p.code}
                  data-testid={`preset-${p.code}`}
                  onClick={() => applyRegionalPreset(p)}
                  title={active
                    ? `This region matches your project currency (${currencyHint}). Rates already follow these defaults.`
                    : `Switch rates to ${p.label} (${p.currency})`}
                  className={`text-xs rounded-md px-3 py-2 transition-colors flex items-center gap-2 border ${
                    active
                      ? "bg-sage-50 border-sage-300 ring-2 ring-sage-200 text-sage-700"
                      : "bg-white border-black/[0.08] hover:border-black/[0.25] hover:bg-[#F5F5F4] text-[#27272A]"
                  }`}
                >
                  <span className="font-medium">{p.label}</span>
                  <span className={`text-[10px] uppercase tracking-[0.15em] ${active ? "text-sage-700" : "text-[#A8A29E]"}`}>{p.currency}</span>
                  {active && (
                    <span className="text-[9px] uppercase tracking-[0.15em] bg-sage-600 text-white rounded-full px-1.5 py-0.5 ml-1" data-testid={`preset-${p.code}-active-pill`}>
                      Active
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {currencyHint && (
            <p className="text-[11px] text-[#A8A29E] mt-3">
              Your project currency is <strong className="text-[#1C1C1C]">{currencyHint}</strong>, so rates already reflect the matching regional defaults automatically. Clicking the active preset will <em>reset</em> any custom rate overrides you've made.
            </p>
          )}
        </div>

        <Tabs defaultValue="rooms" className="w-full">
          <TabsList className="bg-white border border-black/[0.08] rounded-lg p-1">
            <TabsTrigger data-testid="library-tab-rooms" value="rooms" className="data-[state=active]:bg-[#1C1C1C] data-[state=active]:text-white text-sm">Rooms</TabsTrigger>
            <TabsTrigger data-testid="library-tab-exterior" value="exterior" className="data-[state=active]:bg-[#1C1C1C] data-[state=active]:text-white text-sm">Exterior</TabsTrigger>
            <TabsTrigger data-testid="library-tab-systems" value="systems" className="data-[state=active]:bg-[#1C1C1C] data-[state=active]:text-white text-sm">Systems</TabsTrigger>
          </TabsList>

          <TabsContent value="rooms" className="mt-6 space-y-4">
            {Object.entries(ROOM_DEFAULTS).map(([roomType, items]) => (
              <RoomLibSection
                key={roomType}
                roomType={roomType}
                items={items}
                overrides={lib.room_rates?.[roomType] || {}}
                customs={lib.custom_room_items?.[roomType] || []}
                currency={currencyHint}
                onRateChange={setRoomItemRate}
                onResetRate={resetRoomItemRate}
                onAddCustom={addCustomItem}
                onUpdateCustom={updateCustomItem}
                onDeleteCustom={deleteCustomItem}
              />
            ))}
          </TabsContent>

          <TabsContent value="exterior" className="mt-6">
            <RateGrid
              items={EXTERIOR_CATEGORIES}
              overrides={lib.exterior_rates || {}}
              currency={currencyHint}
              onChange={setExteriorRate}
              onReset={resetExteriorRate}
              tidPrefix="lib-ext"
            />
          </TabsContent>

          <TabsContent value="systems" className="mt-6">
            <RateGrid
              items={SYSTEMS_CATEGORIES}
              overrides={lib.systems_rates || {}}
              currency={currencyHint}
              onChange={setSystemsRate}
              onReset={resetSystemsRate}
              tidPrefix="lib-sys"
            />
          </TabsContent>
        </Tabs>
    </div>
  );
}

function RoomLibSection({
  roomType, items, overrides, customs, currency,
  onRateChange, onResetRate, onAddCustom, onUpdateCustom, onDeleteCustom,
}) {
  return (
    <div className="fc-card p-5" data-testid={`lib-room-${roomType}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="fc-heading text-[#1C1C1C] capitalize text-[15px]">{roomType}</div>
        <button
          data-testid={`lib-room-add-${roomType}`}
          onClick={() => onAddCustom(roomType)}
          className="text-xs text-[#78716C] hover:text-[#1C1C1C] border border-black/[0.08] hover:border-black/[0.2] rounded-md px-2.5 py-1.5 transition-colors flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> Add Custom Item
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        {items.map(({ name, unit, kind, rate: defaultRate }) => {
          const override = overrides[name];
          const derivedDefault = scaledDefault(defaultRate, currency, kind);
          const val = override ?? derivedDefault;
          // A row is only "modified" when the override genuinely differs from
          // the per-kind derived default. This prevents the Reset button from
          // lighting up on every row after a regional preset (the old preset
          // logic stored the same per-kind value as an override).
          const isOverride = override !== undefined && override !== derivedDefault;
          return (
            <div key={name}>
              <div className="fc-label mb-1 truncate flex items-baseline gap-1.5" title={name}>
                <span className="truncate">{name}</span>
                <span className="text-[9px] text-[#A8A29E] normal-case tracking-normal whitespace-nowrap">
                  · {currency ? `${currency} / ${unit}` : `per ${unit}`}
                </span>
              </div>
              <div className="relative flex items-center gap-1">
                <Input
                  data-testid={`lib-room-rate-${roomType}-${name}`}
                  type="number"
                  value={val}
                  onChange={(e) => onRateChange(roomType, name, Number(e.target.value) || 0)}
                  className="fc-input h-9 rounded-md text-sm pr-6"
                />
                {isOverride && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-[#059669] pointer-events-none">●</span>
                )}
                {isOverride && (
                  <button
                    type="button"
                    data-testid={`lib-room-reset-${roomType}-${name}`}
                    onClick={() => onResetRate(roomType, name)}
                    title="Reset to default"
                    aria-label={`Reset ${name} to default`}
                    className="h-9 w-9 grid place-items-center text-[#A8A29E] hover:text-[#1C1C1C] hover:bg-black/[0.04] rounded-md transition-colors shrink-0"
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {customs.length > 0 && (
        <div className="mt-5 pt-4 border-t border-black/[0.06]">
          <div className="fc-label mb-3">Custom Items</div>
          <div className="space-y-2">
            {customs.map((ci) => (
              <div
                key={ci.id}
                className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center"
                data-testid={`lib-custom-row-${roomType}-${ci.id}`}
              >
                <div className="col-span-2 sm:col-span-5">
                  <Input
                    data-testid={`lib-custom-name-${ci.id}`}
                    value={ci.name}
                    onChange={(e) => onUpdateCustom(roomType, ci.id, { name: e.target.value })}
                    placeholder="Item name"
                    className="fc-input h-9 rounded-md text-sm"
                  />
                </div>
                <div className="col-span-1 sm:col-span-3">
                  <select
                    data-testid={`lib-custom-unit-${ci.id}`}
                    value={ci.unit}
                    onChange={(e) => onUpdateCustom(roomType, ci.id, { unit: e.target.value })}
                    className="fc-input h-9 rounded-md text-sm px-2 w-full"
                  >
                    {UNIT_OPTIONS.map((u) => <option key={u} value={u}>per {u}</option>)}
                  </select>
                </div>
                <div className="col-span-1 sm:col-span-3">
                  <div className="relative">
                    <Input
                      data-testid={`lib-custom-rate-${ci.id}`}
                      type="number"
                      value={ci.rate}
                      onChange={(e) => onUpdateCustom(roomType, ci.id, { rate: Number(e.target.value) || 0 })}
                      className="fc-input h-9 rounded-md text-sm pl-5"
                    />
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-[#A8A29E]">$</span>
                  </div>
                </div>
                <button
                  data-testid={`lib-custom-delete-${ci.id}`}
                  onClick={() => onDeleteCustom(roomType, ci.id)}
                  className="col-span-2 sm:col-span-1 text-[#A8A29E] hover:text-[#DC2626] transition-colors justify-self-end sm:justify-self-center"
                  aria-label="Delete custom item"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RateGrid({ items, overrides, currency, onChange, onReset, tidPrefix }) {
  return (
    <div className="fc-card p-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {items.map((cat) => {
          const override = overrides[cat.key];
          const derivedDefault = scaledDefault(cat.baseRate, currency);
          const val = override ?? derivedDefault;
          const isOverride = override !== undefined && override !== derivedDefault;
          return (
            <div key={cat.key}>
              <div className="fc-label mb-1 flex items-baseline gap-1.5">
                <span>{cat.label}</span>
                <span className="text-[9px] text-[#A8A29E] normal-case tracking-normal whitespace-nowrap">
                  · {currency ? `${currency} / ${cat.unit}` : `per ${cat.unit}`}
                </span>
              </div>
              <div className="relative flex items-center gap-1">
                <Input
                  data-testid={`${tidPrefix}-rate-${cat.key}`}
                  type="number"
                  value={val}
                  onChange={(e) => onChange(cat.key, Number(e.target.value) || 0)}
                  className="fc-input h-10 rounded-md text-sm pr-6"
                />
                {isOverride && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#059669] pointer-events-none">●</span>
                )}
                {isOverride && onReset && (
                  <button
                    type="button"
                    data-testid={`${tidPrefix}-reset-${cat.key}`}
                    onClick={() => onReset(cat.key)}
                    title="Reset to default"
                    aria-label={`Reset ${cat.label} to default`}
                    className="h-10 w-10 grid place-items-center text-[#A8A29E] hover:text-[#1C1C1C] hover:bg-black/[0.04] rounded-md transition-colors shrink-0"
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
